import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classNames from "../../../utils/classNames";
import {
    formatStyleTagForDisplay,
    normalizeStyleTagList,
} from "../../../utils/styleTags";
import "./index.scss";

function resolveMonogram(band) {
    if (band?.monogram) {
        return band.monogram.toUpperCase();
    }
    const source = (band?.name || "").trim();
    if (!source) {
        return "?";
    }
    const parts = source.split(/\s+/).slice(0, 2);
    return parts.map((part) => part.charAt(0)).join("").toUpperCase();
}

function buildStyleLabel(band) {
    const styles = Array.isArray(band?.styleTags) && band.styleTags.length > 0
        ? band.styleTags
        : [band?.style];
    return normalizeStyleTagList(styles)
        .slice(0, 3)
        .map(formatStyleTagForDisplay)
        .join(" • ");
}

export default function BandCatalogCard({ band, onOpen, className = "", ownerLabel, actions }) {
    const { t } = useTranslation();

    const isInteractive = typeof onOpen === "function";
    const cover = band?.cover || "var(--cover-neutral)";
    const monogram = resolveMonogram(band);
    const imageUrl = typeof band?.imageUrl === "string" ? band.imageUrl.trim() : "";
    const styleLabel = buildStyleLabel(band);
    const locationLabel = typeof band?.location === "string" ? band.location.trim() : "";
    const members = Array.isArray(band?.members) ? band.members : [];
    const numericMemberCount = Number(band?.memberCount);
    const memberCount = Number.isFinite(numericMemberCount)
        ? numericMemberCount
        : members.length;
    const memberCountLabel = t("bandsWelcomePage.card.members", { count: memberCount });
    const resolvedOwnerLabel = ownerLabel || t("home.profileHeader.bands.roleOwner", { defaultValue: "Owner" });

    const activate = () => {
        if (isInteractive) {
            onOpen(band);
        }
    };

    const handleKeyDown = (event) => {
        if (!isInteractive) {
            return;
        }
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            activate();
        }
    };

    return (
        <article
            className={classNames("band-catalog-card", className, {
                "band-catalog-card--has-image": Boolean(imageUrl),
                "band-catalog-card--interactive": isInteractive,
            })}
            tabIndex={isInteractive ? 0 : undefined}
            role={isInteractive ? "button" : undefined}
            aria-label={isInteractive ? band?.name || "" : undefined}
            onClick={isInteractive ? activate : undefined}
            onKeyDown={isInteractive ? handleKeyDown : undefined}
        >
            <div
                className="band-catalog-card__bg"
                style={{ backgroundImage: cover }}
                aria-hidden="true"
            >
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt=""
                        loading="lazy"
                        onError={(event) => {
                            event.currentTarget.style.display = "none";
                        }}
                    />
                ) : null}
            </div>
            {!imageUrl ? (
                <span className="band-catalog-card__monogram" aria-hidden="true">
                    {monogram}
                </span>
            ) : null}
            <span className="band-catalog-card__overlay" aria-hidden="true" />
            {band?.isOwner ? (
                <span className="ed-band-card__owner band-catalog-card__owner">
                    ★ {resolvedOwnerLabel}
                </span>
            ) : null}
            {actions ? (
                <div
                    className="band-catalog-card__actions"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    {actions}
                </div>
            ) : null}

            <div className="band-catalog-card__body">
                {styleLabel ? <p className="band-catalog-card__style">{styleLabel}</p> : null}
                <h3 className="band-catalog-card__name">{band?.name}</h3>
                <small
                    className="band-catalog-card__members-count"
                    aria-label={t("bandsWelcomePage.card.membersAria", { count: memberCount })}
                >
                    {memberCountLabel}
                </small>
            </div>
            {locationLabel ? <div className="band-catalog-card__location">{locationLabel}</div> : null}
        </article>
    );
}

BandCatalogCard.propTypes = {
    band: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        style: PropTypes.string,
        styleTags: PropTypes.arrayOf(PropTypes.string),
        location: PropTypes.string,
        locationSearchText: PropTypes.string,
        description: PropTypes.string,
        cover: PropTypes.string,
        imageUrl: PropTypes.string,
        isOwner: PropTypes.bool,
        currentUserIsMember: PropTypes.bool,
        currentUserMembershipStatus: PropTypes.string,
        monogram: PropTypes.string,
        memberCount: PropTypes.number,
        members: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
            displayName: PropTypes.string,
            username: PropTypes.string,
            avatarUrl: PropTypes.string,
            tint: PropTypes.number,
        })),
    }).isRequired,
    onOpen: PropTypes.func,
    className: PropTypes.string,
    ownerLabel: PropTypes.string,
    actions: PropTypes.node,
};
